/**
 * React Query hooks for Assessment API
 * Based on Assessment API Documentation v6.0.210
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { assessmentService } from '@/services/assessment.service';
import {
  AssessmentAdmin,
  AssessmentLearner,
  CourseWithAssessments,
  ModuleContentTreeResponse,
} from '@/types/assessment.types';

// ============================================================================
// Query Keys
// ============================================================================

export const assessmentKeys = {
  all: ['assessments'] as const,

  // Course-level
  course: (courseId: string) => [...assessmentKeys.all, 'course', courseId] as const,

  // Module-level
  module: (moduleId: string) => [...assessmentKeys.all, 'module', moduleId] as const,

  // Individual assessments
  admin: (assessmentId: string) => [...assessmentKeys.all, 'admin', assessmentId] as const,
  // The language is part of the key. Without it, switching language would
  // return the previous language’s questions straight from cache and never
  // call the API.
  learner: (assessmentId: string, languageCode?: string) =>
    [...assessmentKeys.all, 'learner', assessmentId, languageCode ?? 'master'] as const,

  // Results and analytics (for future use)
  results: (assessmentId: string, attemptId: string) =>
    [...assessmentKeys.all, 'results', assessmentId, attemptId] as const,
  analytics: (assessmentId: string) =>
    [...assessmentKeys.all, 'analytics', assessmentId] as const,
};

// ============================================================================
// Course Assessment Hooks
// ============================================================================

/**
 * Fetch course details with assessment summaries (pre/post at course level)
 *
 * Usage:
 * ```tsx
 * const { data: course, isLoading } = useCourseAssessments(courseId);
 *
 * if (course?.pre_assessment) {
 *   // Show pre-assessment card
 * }
 * ```
 */
export function useCourseAssessments(
  courseId: string,
  options?: {
    enabled?: boolean;
    staleTime?: number;
  }
): UseQueryResult<CourseWithAssessments, Error> {
  return useQuery({
    queryKey: assessmentKeys.course(courseId),
    queryFn: () => assessmentService.getCourseAssessmentSummary(courseId),
    enabled: options?.enabled ?? !!courseId,
    staleTime: options?.staleTime ?? 5 * 60 * 1000, // 5 minutes default
  });
}

// ============================================================================
// Module Assessment Hooks
// ============================================================================

/**
 * Fetch module content tree with all assessment summaries
 * Includes module-level and content-item-level assessments (pre/post)
 *
 * Usage:
 * ```tsx
 * const { data: moduleData, isLoading } = useModuleAssessments(moduleId);
 *
 * const module = moduleData?.module;
 * const contentItems = module?.content_items || [];
 *
 * // Access module-level assessments
 * if (module?.pre_assessment) { }
 *
 * // Access content-item assessments
 * contentItems.forEach(item => {
 *   if (item.pre_assessment) { }
 *   if (item.post_assessment) { }
 * });
 * ```
 */
export function useModuleAssessments(
  moduleId: string,
  options?: {
    enabled?: boolean;
    staleTime?: number;
  }
): UseQueryResult<ModuleContentTreeResponse, Error> {
  return useQuery({
    queryKey: assessmentKeys.module(moduleId),
    queryFn: () => assessmentService.getModuleAssessmentSummary(moduleId),
    enabled: options?.enabled ?? !!moduleId,
    staleTime: options?.staleTime ?? 5 * 60 * 1000, // 5 minutes default
  });
}

// ============================================================================
// Individual Assessment Hooks
// ============================================================================

/**
 * Fetch full assessment with correct answers (ADMIN VIEW ONLY)
 * Includes all questions with correct answers, explanations, and hints
 *
 * Usage:
 * ```tsx
 * const { data: assessment, isLoading } = useAssessmentAdmin(assessmentId);
 *
 * // Access correct answers for review/editing
 * assessment?.questions.forEach(q => {
 *   if (q.question_type === 'SINGLE_CHOICE') {
 *     const correctOption = q.options.find(o => o.is_correct);
 *   }
 * });
 * ```
 */
export function useAssessmentAdmin(
  assessmentId: string,
  options?: {
    enabled?: boolean;
    staleTime?: number;
  }
): UseQueryResult<AssessmentAdmin, Error> {
  return useQuery({
    queryKey: assessmentKeys.admin(assessmentId),
    queryFn: () => assessmentService.getAssessmentAdmin(assessmentId),
    enabled: options?.enabled ?? !!assessmentId,
    staleTime: options?.staleTime ?? 10 * 60 * 1000, // 10 minutes default
  });
}

/**
 * Fetch sanitized assessment for learners (NO CORRECT ANSWERS)
 * Includes only questions and options, no answers/explanations
 *
 * Usage:
 * ```tsx
 * const { data: assessment, isLoading } = useAssessmentLearner(assessmentId);
 *
 * // Display questions to learner (no correct answers exposed)
 * assessment?.questions.forEach(q => {
 *   // Render question without showing correct answers
 * });
 * ```
 */
export function useAssessmentLearner(
  assessmentId: string,
  languageCode?: string,
  options?: {
    enabled?: boolean;
    staleTime?: number;
    refetchOnMount?: boolean;
  }
): UseQueryResult<AssessmentLearner, Error> {
  return useQuery({
    queryKey: assessmentKeys.learner(assessmentId, languageCode),
    queryFn: () => assessmentService.getAssessmentLearner(assessmentId, languageCode),
    enabled: options?.enabled ?? !!assessmentId,
    staleTime: options?.staleTime ?? 0, // Always fetch fresh for learner view
    refetchOnMount: options?.refetchOnMount ?? true, // Always refetch on mount
  });
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Check if a course has any assessments (pre or post)
 *
 * Usage:
 * ```tsx
 * const hasAssessments = useCourseHasAssessments(courseId);
 *
 * if (hasAssessments) {
 *   // Show assessments tab
 * }
 * ```
 */
export function useCourseHasAssessments(courseId: string): boolean {
  const { data: course } = useCourseAssessments(courseId);
  return !!(course?.pre_assessment || course?.post_assessment);
}

/**
 * Check if a module has any assessments (module-level or content-item-level)
 *
 * Usage:
 * ```tsx
 * const hasAssessments = useModuleHasAssessments(moduleId);
 *
 * if (hasAssessments) {
 *   // Show assessments section
 * }
 * ```
 */
export function useModuleHasAssessments(moduleId: string): boolean {
  const { data: moduleData } = useModuleAssessments(moduleId);

  if (!moduleData) return false;

  const module = moduleData.module;

  // Check module-level assessments
  if (module?.pre_assessment || module?.post_assessment) {
    return true;
  }

  // Check content-item-level assessments
  const hasContentItemAssessments = module?.content_items?.some(
    item => item.pre_assessment || item.post_assessment
  );

  return !!hasContentItemAssessments;
}

/**
 * Get total question count across all assessments in a module
 *
 * Usage:
 * ```tsx
 * const totalQuestions = useModuleTotalQuestions(moduleId);
 * // Returns: 45 (sum of all pre/post assessments)
 * ```
 */
export function useModuleTotalQuestions(moduleId: string): number {
  const { data: moduleData } = useModuleAssessments(moduleId);

  if (!moduleData) return 0;

  const module = moduleData.module;
  let total = 0;

  // Add module-level questions
  if (module?.pre_assessment) {
    total += module.pre_assessment.total_questions;
  }
  if (module?.post_assessment) {
    total += module.post_assessment.total_questions;
  }

  // Add content-item-level questions
  module?.content_items?.forEach(item => {
    if (item.pre_assessment) {
      total += item.pre_assessment.total_questions;
    }
    if (item.post_assessment) {
      total += item.post_assessment.total_questions;
    }
  });

  return total;
}
